import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API, useAuth } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import {
  Search, UserCog, DollarSign, Clock, Settings, CalendarPlus, Percent,
} from "lucide-react";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

export const AdminPricingPage = () => {
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedAdmin, setSelectedAdmin] = useState(null);
  const [subscriptionData, setSubscriptionData] = useState(null);
  const [pricingForm, setPricingForm] = useState({
    custom_otp_price: "",
    custom_commission_rate: "",
    notes: "",
  });
  const [trialDays, setTrialDays] = useState(14);
  const [globalSettings, setGlobalSettings] = useState({
    global_otp_price: 0.5,
    global_commission_rate: 10,
  });

  useEffect(() => { 
    fetchAdmins(); 
    fetchGlobalSettings();
  }, []);

  const fetchAdmins = async () => {
    try {
      const res = await axios.get(`${API}/superadmin/admins`);
      setAdmins(res.data);
    } catch (err) {
      toast.error("Failed to load admins");
    } finally {
      setLoading(false);
    }
  };

  const fetchGlobalSettings = async () => {
    try {
      const res = await axios.get(`${API}/superadmin/global-settings`);
      if (res.data) {
        setGlobalSettings({
          global_otp_price: res.data.otp_price || 0.5,
          global_commission_rate: res.data.commission_rate || 10,
        });
      }
    } catch (err) {
      console.warn('Failed to fetch global settings, using defaults:', err.message);
    }
  };

  const fetchAdminSubscription = async (adminId) => {
    try {
      const res = await axios.get(`${API}/superadmin/admins/${adminId}/subscription`);
      setSubscriptionData(res.data);
      setPricingForm({
        custom_otp_price: res.data.custom_otp_price?.toString() || "",
        custom_commission_rate: res.data.custom_commission_rate?.toString() || "",
        notes: res.data.notes || "",
      });
    } catch (err) {
      toast.error("Failed to load subscription details");
    }
  };

  const handleSelectAdmin = async (admin) => {
    setSelectedAdmin(admin);
    await fetchAdminSubscription(admin.id);
  };

  const handleUpdatePricing = async () => {
    if (!selectedAdmin) return;
    try {
      await axios.put(`${API}/superadmin/admins/${selectedAdmin.id}/subscription`, {
        custom_otp_price: pricingForm.custom_otp_price ? parseFloat(pricingForm.custom_otp_price) : null,
        custom_commission_rate: pricingForm.custom_commission_rate ? parseFloat(pricingForm.custom_commission_rate) : null,
        notes: pricingForm.notes,
      });
      toast.success("Admin pricing updated successfully");
      fetchAdminSubscription(selectedAdmin.id);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update pricing");
    }
  };

  const handleExtendTrial = async () => {
    if (!selectedAdmin || trialDays < 1) return;
    try {
      await axios.post(`${API}/superadmin/admins/${selectedAdmin.id}/extend-trial?days=${trialDays}`);
      toast.success(`Trial extended by ${trialDays} days`);
      fetchAdminSubscription(selectedAdmin.id);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to extend trial");
    }
  };

  const handleResetToGlobal = async () => {
    if (!selectedAdmin) return;
    try {
      await axios.put(`${API}/superadmin/admins/${selectedAdmin.id}/subscription`, {
        custom_otp_price: null,
        custom_commission_rate: null,
        notes: pricingForm.notes + "\n[Reset to global pricing]",
      });
      toast.success("Pricing reset to global defaults");
      fetchAdminSubscription(selectedAdmin.id);
    } catch (err) {
      toast.error("Failed to reset pricing");
    }
  };

  const filteredAdmins = admins.filter(admin =>
    admin.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    admin.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div data-testid="admin-pricing-page">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-foreground">Admin Pricing & Subscriptions</h2>
        <p className="text-muted-foreground">
          Set custom OTP charges and commission rates for individual admins
        </p>
      </div>

      {/* Global Defaults Banner */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
        <div className="flex items-start gap-3">
          <Settings className="w-5 h-5 text-blue-600 mt-0.5" />
          <div>
            <p className="font-medium text-blue-800">Global Default Pricing</p>
            <p className="text-sm text-blue-600">
              OTP Price: ₹{globalSettings.global_otp_price.toFixed(2)} per SMS | 
              Commission Rate: {globalSettings.global_commission_rate}%
            </p>
            <p className="text-xs text-blue-500 mt-1">
              Custom pricing for an admin will override these defaults
            </p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Admin List */}
        <div className="lg:col-span-1">
          <div className="bg-card rounded-xl border border-border overflow-hidden">
            <div className="p-4 border-b border-border">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input 
                  placeholder="Search admins..." 
                  value={searchTerm} 
                  onChange={(e) => setSearchTerm(e.target.value)} 
                  className="pl-10" 
                />
              </div>
            </div>
            <div className="max-h-[60vh] overflow-y-auto">
              {filteredAdmins.length === 0 ? (
                <div className="p-4 text-center text-muted-foreground">No admins found</div>
              ) : (
                filteredAdmins.map((admin) => (
                  <button
                    key={admin.id}
                    onClick={() => handleSelectAdmin(admin)}
                    className={`w-full p-4 text-left border-b border-border last:border-b-0 transition-colors ${
                      selectedAdmin?.id === admin.id 
                        ? "bg-primary/10 border-l-4 border-l-primary" 
                        : "hover:bg-muted"
                    }`}
                    data-testid={`admin-item-${admin.id}`}
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center">
                        <UserCog className="w-5 h-5 text-purple-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium truncate">{admin.name}</p>
                        <p className="text-xs text-muted-foreground truncate">{admin.email}</p>
                      </div>
                    </div>
                  </button>
                ))
              )}
            </div>
          </div>
        </div>

        {/* Pricing Details */}
        <div className="lg:col-span-2">
          {!selectedAdmin ? (
            <div className="bg-card rounded-xl border border-border p-8 text-center">
              <UserCog className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-lg font-medium text-muted-foreground">Select an admin</p>
              <p className="text-sm text-muted-foreground">
                Click on an admin from the list to view and edit their pricing
              </p>
            </div>
          ) : (
            <div className="bg-card rounded-xl border border-border">
              {/* Admin Header */}
              <div className="p-4 border-b border-border">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-purple-100 flex items-center justify-center">
                    <UserCog className="w-6 h-6 text-purple-600" />
                  </div>
                  <div>
                    <h3 className="font-bold text-lg">{selectedAdmin.name}</h3>
                    <p className="text-sm text-muted-foreground">{selectedAdmin.email}</p>
                  </div>
                </div>
              </div>

              <Tabs defaultValue="pricing" className="p-4">
                <TabsList className="grid w-full grid-cols-2 mb-4">
                  <TabsTrigger value="pricing">
                    <DollarSign className="w-4 h-4 mr-2" /> Pricing
                  </TabsTrigger>
                  <TabsTrigger value="trial">
                    <Clock className="w-4 h-4 mr-2" /> Trial
                  </TabsTrigger>
                </TabsList>

                <TabsContent value="pricing" className="space-y-4">
                  {/* Current Effective Pricing */}
                  {subscriptionData && (
                    <div className="grid grid-cols-2 gap-4 p-4 bg-muted rounded-lg">
                      <div>
                        <p className="text-xs text-muted-foreground">Effective OTP Price</p>
                        <p className="text-xl font-bold text-foreground">
                          ₹{subscriptionData.effective_otp_price?.toFixed(2) || globalSettings.global_otp_price.toFixed(2)}
                          {subscriptionData.custom_otp_price && (
                            <span className="text-xs font-normal text-green-600 ml-2">Custom</span>
                          )}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">Effective Commission</p>
                        <p className="text-xl font-bold text-foreground">
                          {subscriptionData.custom_commission_rate || globalSettings.global_commission_rate}%
                          {subscriptionData.custom_commission_rate && (
                            <span className="text-xs font-normal text-green-600 ml-2">Custom</span>
                          )}
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Custom Pricing Form */}
                  <div className="space-y-4">
                    <div>
                      <Label>Custom OTP Price (₹ per SMS)</Label>
                      <div className="flex gap-2 mt-1">
                        <Input
                          type="number"
                          step="0.01"
                          min="0"
                          value={pricingForm.custom_otp_price}
                          onChange={(e) => setPricingForm({ ...pricingForm, custom_otp_price: e.target.value })}
                          placeholder={`Global: ₹${globalSettings.global_otp_price}`}
                        />
                      </div>
                      <p className="text-xs text-muted-foreground mt-1">
                        Leave empty to use global default (₹{globalSettings.global_otp_price})
                      </p>
                    </div>

                    <div>
                      <Label>Custom Commission Rate (%)</Label>
                      <div className="flex gap-2 mt-1">
                        <Input
                          type="number"
                          step="0.1"
                          min="0"
                          max="100"
                          value={pricingForm.custom_commission_rate}
                          onChange={(e) => setPricingForm({ ...pricingForm, custom_commission_rate: e.target.value })}
                          placeholder={`Global: ${globalSettings.global_commission_rate}%`}
                        />
                      </div>
                      <p className="text-xs text-muted-foreground mt-1">
                        Leave empty to use global default ({globalSettings.global_commission_rate}%)
                      </p>
                    </div>

                    <div>
                      <Label>Notes</Label>
                      <Textarea
                        value={pricingForm.notes}
                        onChange={(e) => setPricingForm({ ...pricingForm, notes: e.target.value })}
                        placeholder="Internal notes about this admin's pricing (e.g., 'Enterprise discount', 'Special promotion')"
                        rows={3}
                        className="mt-1"
                      />
                    </div>

                    <div className="flex gap-2 pt-2">
                      <Button onClick={handleUpdatePricing} className="flex-1">
                        Save Pricing
                      </Button>
                      <Button variant="outline" onClick={handleResetToGlobal}>
                        Reset to Global
                      </Button>
                    </div>
                  </div>
                </TabsContent>

                <TabsContent value="trial" className="space-y-4">
                  {/* Current Trial Status */}
                  {subscriptionData && (
                    <div className="p-4 bg-muted rounded-lg">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm text-muted-foreground">Subscription Status</span>
                        <span className={`px-2 py-1 rounded text-xs font-medium ${
                          subscriptionData.subscription_status === 'active' 
                            ? 'bg-green-100 text-green-700'
                            : subscriptionData.subscription_status === 'trial'
                            ? 'bg-blue-100 text-blue-700'
                            : 'bg-yellow-100 text-yellow-700'
                        }`}>
                          {subscriptionData.subscription_status || 'Trial'}
                        </span>
                      </div>
                      {subscriptionData.trial_ends_at && (
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-muted-foreground">Trial Ends</span>
                          <span className="font-mono text-sm">
                            {new Date(subscriptionData.trial_ends_at).toLocaleDateString()}
                          </span>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Extend Trial */}
                  <div className="space-y-4">
                    <div>
                      <Label>Extend Trial Period</Label>
                      <div className="flex gap-2 mt-2">
                        {[7, 14, 30, 60].map((days) => (
                          <Button
                            key={days}
                            variant={trialDays === days ? "default" : "outline"}
                            size="sm"
                            onClick={() => setTrialDays(days)}
                          >
                            {days}d
                          </Button>
                        ))}
                      </div>
                      <div className="flex gap-2 mt-3">
                        <Input
                          type="number"
                          min="1"
                          max="365"
                          value={trialDays}
                          onChange={(e) => setTrialDays(parseInt(e.target.value) || 1)}
                          className="w-24"
                        />
                        <span className="text-sm text-muted-foreground self-center">days</span>
                      </div>
                    </div>

                    <Button onClick={handleExtendTrial} className="w-full">
                      <CalendarPlus className="w-4 h-4 mr-2" />
                      Extend Trial by {trialDays} Days
                    </Button>

                    <p className="text-xs text-muted-foreground text-center">
                      This will add {trialDays} days to the current trial end date
                    </p>
                  </div>
                </TabsContent>
              </Tabs>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminPricingPage;
